import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, Field } from '../components/ui';

export function LoginPage() {
  const { refresh } = useAuth();
  const [setup, setSetup] = useState<{ needsSetup: boolean; setupTokenConfigured: boolean } | null>(null);
  const [form, setForm] = useState({ username: '', password: '', displayName: '', setupToken: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Employees sign in with a 6-digit PIN, administrators with a password. Remembered on this device.
  const [mode, setModeState] = useState<'pin' | 'password'>(() => {
    try {
      return localStorage.getItem('loginMode') === 'password' ? 'password' : 'pin';
    } catch {
      return 'pin';
    }
  });
  const setMode = (m: 'pin' | 'password') => {
    setModeState(m);
    setForm((f) => ({ ...f, password: '' }));
    setError(null);
    try {
      localStorage.setItem('loginMode', m);
    } catch {
      // private mode: not remembered
    }
  };
  const pin = mode === 'pin' && !setup?.needsSetup;

  useEffect(() => {
    api('/auth/setup-status').then(setSetup).catch(setError);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (setup?.needsSetup) await api('/auth/setup', { body: form });
      else await api('/auth/login', { body: { username: form.username, password: form.password } });
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="login">
      <form className="card login-card" onSubmit={submit}>
        <h1>M&amp;A Inventory</h1>
        {setup?.needsSetup ? (
          <>
            <p>
              <strong>First-time setup.</strong> Create the first administrator account. You need the <code>SETUP_TOKEN</code> value from the server's
              secrets.
            </p>
            {!setup.setupTokenConfigured && <div className="alert alert-warn">SETUP_TOKEN is not set on the server yet. Add it to the secrets and restart.</div>}
            <Field label="Setup token">
              <input value={form.setupToken} onChange={set('setupToken')} type="password" autoComplete="off" required />
            </Field>
            <Field label="Your name">
              <input value={form.displayName} onChange={set('displayName')} required />
            </Field>
          </>
        ) : null}
        <Field label="Username">
          <input value={form.username} onChange={set('username')} autoComplete="username" autoCapitalize="off" required />
        </Field>
        {pin ? (
          <Field label="PIN" hint="Your 6-digit PIN">
            <input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              type="password"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="current-password"
              className="pin-input"
              aria-label="6-digit PIN"
              required
            />
          </Field>
        ) : (
          <>
            <Field label="Password" hint={setup?.needsSetup ? 'At least 10 characters' : undefined}>
              <div className="row gap">
                <input
                  value={form.password}
                  onChange={set('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={setup?.needsSetup ? 'new-password' : 'current-password'}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className={showPassword ? 'mono' : undefined}
                  required
                />
                <button type="button" className="btn" onClick={() => setShowPassword((s) => !s)} aria-pressed={showPassword}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>
            {showPassword && /^\s|\s$/.test(form.password) && (
              <div className="alert alert-warn">The password starts or ends with a space. Remove it if that was not intended.</div>
            )}
          </>
        )}
        <ErrorBox error={error} />
        <button className="btn btn-primary btn-block" disabled={busy || !setup || (pin && form.password.length !== 6)}>
          {busy ? 'Please wait…' : setup?.needsSetup ? 'Create administrator' : 'Sign in'}
        </button>
        {!setup?.needsSetup && (
          <button type="button" className="link small login-mode" onClick={() => setMode(pin ? 'password' : 'pin')}>
            {pin ? 'Administrator? Sign in with a password' : 'Sign in with a PIN instead'}
          </button>
        )}
      </form>
    </div>
  );
}
